import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { green, red } from "@mui/material/colors";
import { titleStr } from "../lib/helper";
import { numberFormat } from "../lib/format";
import { useConfig } from "../containers/ConfigContext";
import { getSubmitAnswers } from "../actions/survey.event.action";
import SurveySubmitAnswerView from "../containers/SurveySubmitAnswerView";
import { sentimentMap, SentimentTypeEnum, Sentiment, GetSentimentParam, SentimentSummary } from "../models/sentiment.model";
import { Edit, Download as DownloadIcon } from "@mui/icons-material";
import SentimentKeywordTypeForm from "../containers/SentimentKeywordTypeForm";
import { UserTypeEnum } from "../models/authorize.model";
import CheckPermission from "./CheckPermission";
import { generateSentimentsCsv, getSentiments, getSentimentsSummary, updateSentimentType } from "../actions/sentiment.action";
import { FormRefEvent } from "../models/base.model";
import LoadingButton from "./LoadingButton";

export default function TextAnalysisSentimentsList(props: {
  mobile: boolean;
  filterParams: GetSentimentParam;
}) {
  const { mobile, filterParams } = props;
  const { localized } = useConfig();
  const { summary, loading: loadingSummary } = useSentimentSummary(filterParams);
  const [sentimentType, setSentimentType] = useState<SentimentTypeEnum>(SentimentTypeEnum.all);
  const { sentiments, setSentiments, offset, loadMoreSentiments, isFirstLoadingSentiments, isMoreLoadingSentiments } = useSentiments(filterParams);
  const loadingMoreRef = useRef(null);
  const listRef = useRef(null);
  const hasMore = Boolean(offset);

  const onChanged = (sentiment: Sentiment) => {
    setSentiments((prev) =>
      prev.map((s) => (s.questionKey === sentiment.questionKey && s.submitId === sentiment.submitId ? sentiment : s))
    );
  };

  useEffect(() => {
    if (!loadingMoreRef.current || !listRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreSentiments();
        }
      },
      {
        root: listRef.current,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    observer.observe(loadingMoreRef.current);

    return () => observer.disconnect();
  }, [loadMoreSentiments]);

  const handleDownload = useCallback(async () => {
    const res = await generateSentimentsCsv({ ...filterParams, sentimentType: sentimentType === SentimentTypeEnum.all ? undefined : sentimentType, isAll: true });
    if (res.message) {
      window.webApp.showMessage("success", res.message);
    }
  }, [filterParams, sentimentType])

  return (
    <div className="padding-l-big padding-r-big">
      <Grid sx={{ mb: 2, ml: 2 }} container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" spacing={2} alignItems="flex-end" position="relative" sx={{ opacity: loadingSummary ? 0.5 : 1 }}>
            {loadingSummary && (
              <CircularProgress size={24} sx={{ position: "absolute", left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
            )}
            <Typography
              variant="body1"
              sx={{
                fontSize: mobile ? 36 : 48,
                color: green[500],
                lineHeight: 1,
              }}
            >
              {numberFormat((summary?.all ? summary.positive / summary.all : 0) * 100, 0)}%
            </Typography>
            <div>
              <Typography variant={mobile ? "body1" : "h6"}>{localized("Positive")}</Typography>
              <Typography variant="body2">
                {numberFormat(summary?.positive || 0, 0)} {localized("records")}
              </Typography>
            </div>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" spacing={2} alignItems="flex-end" position="relative" sx={{ opacity: loadingSummary ? 0.5 : 1 }}>
            {loadingSummary && (
              <CircularProgress size={24} sx={{ position: "absolute", left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontSize: mobile ? 36 : 48,
                color: red[500],
                lineHeight: 1,
              }}
            >
              {numberFormat((summary?.all ? summary.negative / summary.all : 0) * 100, 0)}%
            </Typography>
            <div>
              <Typography variant={mobile ? "body1" : "h6"}>{localized("Negative")}</Typography>
              <Typography variant="body2">
                {numberFormat(summary?.negative || 0, 0)} {localized("records")}
              </Typography>
            </div>
          </Stack>
        </Grid>
      </Grid>
      <div>
        <Stack direction="row" spacing={2} alignItems="center" pl={3}>
          <FormControl variant="standard" sx={{ flex: 1 }}>
            <InputLabel>Sentiment</InputLabel>
            <Select
              label={localized("Sentiment")}
              value={sentimentType}
              onChange={(e) => {
                //@ts-ignore
                setSentimentType(e.target.value);
              }}
            >
              <MenuItem value={"all"}>{localized("All")}</MenuItem>
              {Object.keys(sentimentMap).map((v) => (
                <MenuItem key={v} value={v}>
                  {localized(titleStr(v))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LoadingButton variant="contained" color="primary" onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download
          </LoadingButton>
        </Stack>
        <Box height={mobile ? "50vh" : "48vh"} marginTop={1}>
          <List
            sx={{
              height: mobile ? "48vh" : "46vh",
              overflowY: "auto",
            }}
            ref={listRef}
          >
            {sentiments
              ?.filter((s) => sentimentType === SentimentTypeEnum.all || `${s.type}` === sentimentType)
              ?.map((s) => (
                <TextAnalysisSentimentItem
                  key={`${s.submitId}#${s.questionKey}`}
                  sentiment={s}
                  onChanged={onChanged}
                />
              ))}
            {hasMore && (
              <div
                ref={loadingMoreRef}
                style={{
                  height: "24px",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 8,
                }}
              >
                {isMoreLoadingSentiments && <CircularProgress size={24} />}
              </div>
            )}
          </List>
        </Box>
      </div>
    </div>
  );
}

export function TextAnalysisSentimentItem(props: {
  sentiment: Sentiment;
  secondary?: React.ReactNode;
  onChanged: (sentiment: Sentiment) => void;
}) {
  const { sentiment, secondary, onChanged } = props;
  const { setLoading } = useConfig();
  const formRef = useRef<FormRefEvent<Sentiment>>(null);

  return (
    <div style={{ position: "relative" }}>
      <ListItem
        alignItems="flex-start"
        button
        onClick={async () => {
          setLoading(true);
          try {
            const { answers, questions } = await getSubmitAnswers({
              eventId: sentiment.eventId,
              submitId: sentiment.submitId,
            });
            await window.webApp.toggleDialog({
              title: "Survey Answer",
              element: <SurveySubmitAnswerView answers={answers} questions={questions} />,
              minWidth: 800,
              maxWidth: 1024,
              okText: "Close",
              noCancel: true,
              open: true,
              onOk: async () => { },
            });
          } finally {
            setLoading(false);
          }
        }}
      >
        <ListItemAvatar>{sentimentMap[sentiment.type]}</ListItemAvatar>
        <ListItemText primary={sentiment.content} secondary={secondary} />
      </ListItem>
      {
        <CheckPermission roles={[UserTypeEnum.root, UserTypeEnum.admin]}>
          <IconButton
            color="primary"
            size="small"
            onClick={async () => {
              await window.webApp.toggleDialog({
                title: "Edit Sentiment",
                element: <SentimentKeywordTypeForm ref={formRef} defaultValues={sentiment} />,
                onOk: async () => {
                  if (!formRef.current) {
                    throw new Error("Form creation error, please refresh or contact us!");
                  }
                  const value = await formRef.current.onSubmit();
                  const res = await updateSentimentType({
                    id: sentiment.id,
                    type: value.type,
                    keywords: value.keywords,
                    questionKey: sentiment.questionKey,
                    submitId: sentiment.submitId,
                  });
                  onChanged(res.sentiment);
                  return res;
                },
                minWidth: 1024,
                maxWidth: 1240,
                open: true,
              });
            }}
            sx={{ position: "absolute", bottom: 12, right: 0 }}
          >
            <Edit />
          </IconButton>
        </CheckPermission>
      }

      <Divider variant="inset" component="li" />
    </div>
  );
}

function useSentiments(filter: GetSentimentParam) {
  const [sentiments, setSentiments] = useState<Sentiment[]>([]);
  const [offset, setOffset] = useState<Record<string, any>>();
  const [isFirstLoading, setIsFirstLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);

  const loadData = useCallback(async (params: GetSentimentParam & {
    needTotal?: boolean;
    offset?: Record<string, any>;
    ignore?: boolean;
  }) => {
    const { ignore, ...restParams } = params

    return getSentiments(restParams).then(res => {
      if (!ignore) {
        if (restParams.offset) {
          setSentiments(prev => prev.concat(res.sentiments));
        } else {
          setSentiments(res.sentiments);
        }
        setOffset(res.offset);
      }
      return res;
    }).catch(error => {
      console.error(error);
      if (!ignore) {
        setOffset(undefined)
      }
      return {
        sentiments: [],
        offset: undefined,
      };
    })
  }, [])

  const loadMoreData = useCallback(async () => {
    if (!offset || isMoreLoading || !filter.id) return;
    setIsMoreLoading(true)
    return await loadData({ ...filter, offset, ignore: false })
      .finally(() => {
        setIsMoreLoading(false);
      })
  }, [filter, offset, loadData, isMoreLoading])

  useEffect(() => {
    if (!filter.id) return;

    let ignore = false;

    setIsFirstLoading(true)
    loadData({ ...filter, ignore }).finally(() => {
      if (!ignore) {
        setIsFirstLoading(false);
      }
    })

    return () => {
      ignore = true;
    };
  }, [filter, loadData])

  return {
    sentiments,
    setSentiments,
    offset,
    isFirstLoadingSentiments: isFirstLoading,
    isMoreLoadingSentiments: isMoreLoading,
    loadMoreSentiments: loadMoreData
  };
}

function useSentimentSummary(filter: GetSentimentParam) {
  const [summary, setSummary] = useState<SentimentSummary | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filter.id) return;
    let ignore = false;

    setLoading(true);
    getSentimentsSummary(filter).then(res => {
      if (!ignore) {
        setSummary(res);
      }
    }).finally(() => {
      if (!ignore) {
        setLoading(false);
      }
    });

    return () => {
      ignore = true;
    };
  }, [filter])

  return { summary, loading };
}
